// src/components/admin/settings/AssociationSettings.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';
import { AlertCircle, Calendar, Building2, Users, MapPin, Phone, Mail } from 'lucide-react';
import { associationsApi } from '../../../api/associations-api';

const AssociationSettings = () => {
  const [associations, setAssociations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentAssociation, setCurrentAssociation] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    nomAssociation: '',
    adresse: '',
    nomContact: '',
    prenomContact: '',
    telContact: '',
    piecesJointes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch associations data on component mount
  useEffect(() => {
    fetchAssociations();
  }, []);

  const fetchAssociations = async () => {
    try {
      setIsLoading(true);
      const data = await associationsApi.getAssociations();
      
      // Add computed evenementsCount field
      const associationsWithCounts = data.map(association => ({
        ...association,
        evenementsCount: association.evenements ? association.evenements.length : 0
      }));
      
      setAssociations(associationsWithCounts);
    } catch (error) {
      console.error('Error fetching associations:', error);
      toast.error('Erreur lors du chargement des associations');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      Header: 'Nom de l\'association',
      accessor: 'nomAssociation',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Building2 size={16} className="mr-2 text-blue-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      Header: 'Contact',
      accessor: 'contact',
      sortable: false,
      searchable: true,
      Cell: ({ row }) => (
        <div className="space-y-1">
          <div className="flex items-center">
            <Users size={12} className="mr-1 text-gray-400" />
            <span className="text-sm font-medium">{row.prenomContact} {row.nomContact}</span>
          </div>
          <div className="flex items-center">
            <Phone size={12} className="mr-1 text-gray-400" />
            <span className="text-xs text-gray-600">{row.telContact}</span>
          </div>
        </div>
      )
    },
    {
      Header: 'Adresse',
      accessor: 'adresse',
      sortable: false,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-start max-w-xs">
          <MapPin size={12} className="mr-1 text-gray-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-gray-600 truncate" title={value}>
            {value.length > 50 ? `${value.substring(0, 50)}...` : value}
          </span>
        </div>
      )
    },
    {
      Header: 'Événements',
      accessor: 'evenementsCount',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Calendar size={14} className="mr-1 text-green-500" />
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            value > 0 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {value}
          </span>
        </div>
      )
    }
  ];

  const handleCreateNew = () => {
    setCurrentAssociation(null);
    setFormData({
      nomAssociation: '',
      adresse: '',
      nomContact: '',
      prenomContact: '',
      telContact: '',
      piecesJointes: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (association) => {
    setCurrentAssociation(association);
    setFormData({
      nomAssociation: association.nomAssociation || '',
      adresse: association.adresse || '',
      nomContact: association.nomContact || '',
      prenomContact: association.prenomContact || '',
      telContact: association.telContact || '',
      piecesJointes: association.piecesJointes || ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (association) => {
    setCurrentAssociation(association);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentAssociation) return;
    
    try {
      setIsSubmitting(true);
      const success = await associationsApi.removeAssociation(currentAssociation.id);
      
      if (success) {
        // Update local state
        setAssociations(associations.filter(a => a.id !== currentAssociation.id));
        setIsConfirmDeleteOpen(false);
        toast.success('Association supprimée avec succès');
      } else {
        toast.error('Erreur lors de la suppression de l\'association');
      }
    } catch (error) {
      console.error('Error deleting association:', error);
      toast.error('Erreur lors de la suppression de l\'association');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nomAssociation.trim()) {
      errors.nomAssociation = 'Le nom de l\'association est requis';
    }
    
    if (!formData.adresse.trim()) {
      errors.adresse = 'L\'adresse est requise';
    }
    
    if (!formData.nomContact.trim()) {
      errors.nomContact = 'Le nom du contact est requis';
    }
    
    if (!formData.prenomContact.trim()) {
      errors.prenomContact = 'Le prénom du contact est requis';
    }
    
    if (!formData.telContact.trim()) {
      errors.telContact = 'Le téléphone du contact est requis';
    } else if (!/^(\+212|0)[5-7][0-9]{8}$/.test(formData.telContact.replace(/\s/g, ''))) {
      errors.telContact = 'Format de téléphone invalide (ex: +212 5XX XXX XXX)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      let result;
      
      if (currentAssociation) {
        // Update existing association
        result = await associationsApi.updateAssociation({
          id: currentAssociation.id,
          ...formData
        });
        
        // Update local state
        setAssociations(associations.map(a => 
          a.id === currentAssociation.id 
            ? { ...result, evenementsCount: currentAssociation.evenementsCount }
            : a
        ));
        toast.success('Association modifiée avec succès');
      } else {
        // Create new association
        result = await associationsApi.createAssociation(formData);
        
        // Add to local state
        setAssociations([...associations, { ...result, evenementsCount: 0 }]);
        toast.success('Association créée avec succès');
      }
      
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving association:', error);
      toast.error(`Erreur lors de ${currentAssociation ? 'la modification' : 'la création'} de l'association`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real application, you would upload the file to a server
      // For now, we just store the filename
      setFormData(prev => ({
        ...prev,
        piecesJointes: file.name
      }));
    }
  };

  return (
    <div>
      <DataTableSettings
        title="Gestion des Associations Représentants"
        subtitle="Gérez les associations et syndicats de pharmaciens"
        data={associations}
        columns={columns}
        loading={isLoading}
        onCreate={handleCreateNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      
      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentAssociation ? 'Modifier l\'association' : 'Créer une nouvelle association'}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'association <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nomAssociation"
              value={formData.nomAssociation}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.nomAssociation ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Association des Pharmaciens du Grand Casablanca"
            />
            {formErrors.nomAssociation && (
              <p className="mt-1 text-sm text-red-600">{formErrors.nomAssociation}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse <span className="text-red-500">*</span>
            </label>
            <textarea
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              rows={2}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.adresse ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="123 Boulevard Mohammed V, Casablanca"
            ></textarea>
            {formErrors.adresse && (
              <p className="mt-1 text-sm text-red-600">{formErrors.adresse}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nomContact"
                value={formData.nomContact}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.nomContact ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="El Ouardi"
              />
              {formErrors.nomContact && (
                <p className="mt-1 text-sm text-red-600">{formErrors.nomContact}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom du contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="prenomContact"
                value={formData.prenomContact}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.prenomContact ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Mohammed"
              />
              {formErrors.prenomContact && (
                <p className="mt-1 text-sm text-red-600">{formErrors.prenomContact}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone du contact <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="telContact"
              value={formData.telContact}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                formErrors.telContact ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="+212 522 987 654"
            />
            {formErrors.telContact && (
              <p className="mt-1 text-sm text-red-600">{formErrors.telContact}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Format accepté: +212 5XX XXX XXX ou 05XX XXX XXX
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documents annexes
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload-association"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-green-700 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                  >
                    <span>Ajouter un fichier</span>
                    <input
                      id="file-upload-association"
                      name="piecesJointes"
                      type="file"
                      className="sr-only"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">ou glisser-déposer</p>
                </div>
                <p className="text-xs text-gray-500">PDF, DOCX, JPG, PNG jusqu'à 10MB</p>
                {formData.piecesJointes && (
                  <div className="mt-2">
                    <p className="text-sm text-green-700 font-medium">{formData.piecesJointes}</p>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, piecesJointes: '' }))}
                      className="text-xs text-red-600 hover:text-red-500 mt-1"
                    >
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form submission info */}
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <div className="flex items-start">
              <AlertCircle size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Information</p>
                <p className="mt-1">
                  Cette association sera utilisée pour organiser des événements avec les laboratoires partenaires.
                  Assurez-vous que les informations de contact sont correctes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ModalComponent>
      
      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
        onSubmit={confirmDelete}
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
        submitButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle size={24} />
            <h3 className="text-lg font-medium">Attention</h3>
          </div>
          
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer l'association{' '}
            <strong className="text-gray-900">{currentAssociation?.nomAssociation}</strong> ?
          </p>
          
          <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
            <div className="flex items-start">
              <AlertCircle size={16} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Cette action est irréversible</p>
                <p className="mt-1">
                  Tous les événements associés à cette association seront également supprimés.
                </p>
              </div>
            </div>
          </div>
          
          {currentAssociation && currentAssociation.evenementsCount > 0 && (
            <div className="bg-red-50 p-3 rounded border border-red-200">
              <div className="flex items-start">
                <Calendar size={16} className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-medium">
                    Cette association a {currentAssociation.evenementsCount} événement(s) associé(s)
                  </p>
                  <p className="mt-1">
                    Ces événements seront également supprimés définitivement.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-sm text-gray-600">
              <p><span className="font-medium">Contact:</span> {currentAssociation?.prenomContact} {currentAssociation?.nomContact}</p>
              <p><span className="font-medium">Téléphone:</span> {currentAssociation?.telContact}</p>
              <p><span className="font-medium">Adresse:</span> {currentAssociation?.adresse}</p>
            </div>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default AssociationSettings;